import { BCMSDefaultModel, BCMSCurrency, BCMSDate, BCMSRecord, BCMSStateManagerCRUD, useBCMSContext, useRouteData, useStateManager } from "@bcm-dev/bcms-vite";
import { PageContainer, PageContentView, WidgetContainer, WidgetView } from 'components/shared';
import Classes, { Account, PositivePayCheck } from "model";
import React, { useEffect } from 'react';
import TASPositivePayCheckForm from "../TASPositivePayAddChecksPage/classes/TASPositivePayCheckForm";
import "./styles.css";
import "./mobile.css";
import TASPositivePaySubmittedChecksDesktopView from "./components/TASPositivePaySubmittedChecksDesktopView";
import TASPositivePaySubmittedChecksMobileView from "./components/TASPositivePaySubmittedChecksMobileView";

/**
 * @function TASPositivePaySubmittedChecksPage
 * @return {React.Component}
 * @exported true
 * @description The TAS Positive Pay Add Check page.
 */
export default function TASPositivePaySubmittedChecksPage(props) {
  const context = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, context);

  const data = { ...getDefaultData(undefined, context), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    updateData: data => {
      const checkForms = data.checkForms.reduce((acc, item) => {
        acc.push(TASPositivePayCheckForm.init({ ...item, account: item.account._id }));
        return acc;
      }, []);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], checkForms)
      .submit(setState);
    },

    onDesktopChange: (id, prop) => val => {
      const checkForms = state.data.checkForms;
      const findSelectedIndex = checkForms.findIndex(item => item.id === id);
      const selectedCheckform = checkForms.filter(item => item.id === id)[0];
      const filteredCheckForms = checkForms.filter(item => item.id !== id);
      const currCheckFormRecord = { ...selectedCheckform.record };
      currCheckFormRecord[prop] = val;
      currCheckFormRecord["mobileViewIsEditing"] = true;
      currCheckFormRecord["mobileViewIsOpen"] = false;
      const updatedCheckForm = TASPositivePayCheckForm.init({ ...currCheckFormRecord, account: currCheckFormRecord.account.id });
      
      const updatedCheckForms = [ 
        ...filteredCheckForms.slice(0, findSelectedIndex), 
        updatedCheckForm, 
        ...filteredCheckForms.slice(findSelectedIndex) 
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], updatedCheckForms)
      .submit(setState);
    },

    onMobileSave: (id, stateEditCheckForms) => val => {
      var editCheckForms = stateEditCheckForms;
      var findSelectedIndex = editCheckForms.findIndex(item => item.id === id);
      var selectedEditCheckForm = editCheckForms[findSelectedIndex];
      const currCheckFormRecord = { 
        ...selectedEditCheckForm.record,
         account: selectedEditCheckForm.account.id,
        mobileViewIsEditing: false 
      };     

      const defaultCheck = BCMSDefaultModel.positivePayCheck();
      const defaultCheckRecord = defaultCheck.record;
      const merge = BCMSDefaultModel.mergeRecords(currCheckFormRecord, defaultCheckRecord);

      const updatedEditCheckForm = TASPositivePayCheckForm.init(merge);

      const currCheckForms = state.data.checkForms;
      const filteredCheckForms = currCheckForms.filter(item => item.id !== id);

      const updatedCheckForms = [ 
        ...filteredCheckForms.slice(0, findSelectedIndex), 
        updatedEditCheckForm, 
        ...filteredCheckForms.slice(findSelectedIndex) 
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], updatedCheckForms)
      .submit(setState);
    },

    onAddChecksClick: evt => {
      let checkForms = state.data.checkForms;
      let addedCheckForm = getDefaultData().checkForms[0];
      let largestID = checkForms.reduce((acc, item) => {
        let accID = Number(acc);
        let id = Number(item.id);
        if(id > accID) {
          acc = `${id}`;
        }
        return acc;
      }, "0");

      largestID = Number(largestID) + 1;
      addedCheckForm["id"] = `${ largestID }`;

      checkForms = checkForms.reduce((acc, item) => {
        const updatedItem = TASPositivePayCheckForm.init({ ...item.record, mobileViewIsEditing: true, mobileViewIsOpen: false });
        acc.push(updatedItem);
        return acc;
      }, []);

      checkForms = [ 
        ...checkForms,
        addedCheckForm
      ];

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "checkForms"], checkForms)
      .submit(setState);
    },

    onDeleteClick: id => evt => {
      const checkForms = state.data.checkForms;
      const filteredCheckForms = checkForms.filter(item => item.id !== id);

      BCMSStateManagerCRUD.init(state)
      .update([ "data", "form", "checkForms"], filteredCheckForms)
      .submit(setState);
    },

    onSubmitClick: evt => {
      const checkForms = state.data.checkForms;
      let submittedChecks = [];

      const createdChecks = checkForms.reduce((acc, item) => {
        const defaultCheck = BCMSDefaultModel.positivePayCheck();
        const defaultCheckRecord = defaultCheck.record;
        const merge = BCMSDefaultModel.mergeRecords(item.record, defaultCheckRecord);
        const updatedEditCheckForm = TASPositivePayCheckForm.init(merge);
        submittedChecks.push(updatedEditCheckForm);
        acc.push(
          PositivePayCheck.create({ ...updatedEditCheckForm.record, account: updatedEditCheckForm.account.id })
        );
        return acc;
      }, []);

      BCMSRecord.batch(createdChecks, context, Classes);
      var currData = { data: { checkForms: submittedChecks } };
      nextStep(currData);
    }
  });

  return (
    <PageContainer className="tas-positive-pay-submitted-checks-page" state={ { data, ui } } controller={ controller }>
      <PageContent />
    </PageContainer>
  );
}

export const getDefaultData = () => {
  const accounts = Account.initAllBusinessAssetOnly().filter(item => item.type === "checking");
  const selectedAccount = accounts[0] ?? {};
  var defaultCheckForm = TASPositivePayCheckForm.init({ account: selectedAccount.id });
  const checkForms = [defaultCheckForm];

  return {
    accounts: accounts,
    selectedAccount: selectedAccount,
    checkForms: checkForms,
    isReviewSubmittedChecksEnabled: false,
    isSubmitButtonEnabled: true
  }
};

export const getDefaultUI = () => {
  return {
    cancelModal: { isActive: false },
  };
};

function PageContent(props) {
  const { data, ui, updateData } = useStateManager();
  const { route } = useRouteData();

  // Data is passed to this page through a route, so set the default data here.
  if(route) {
    window.history.replaceState({}, document.title);
    updateData(route.data);
  }

  // Manually add the active class to the "Accounts" main nav item.
  useEffect(() => {
    const navItem = document.querySelector(`[data-point="Nav_Accounts"] a`);
    
    if(navItem) {
      navItem.classList.add("active-item");
    }
  }, []);

  return (
    <PageContentView>
         <WidgetContainer className="tas-positive-pay-submitted-checks-widget">
            <WidgetView text="Submitted Checks">
              { <TASPositivePaySubmittedChecksDesktopView edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
              { <TASPositivePaySubmittedChecksMobileView edit={ { editCheckForms: data.editCheckForms } }
              cancelModal={ ui.cancelModal } /> }
            </WidgetView>
        </WidgetContainer>
    </PageContentView>
  );
}